<!--
  Der Akten-Safe, eingebettet und schon aufgeschlossen.

  Der Kunde hat sein Passwort beim Anmelden eingegeben. Daraus liegt hier noch
  der Hauptschlüssel im Arbeitsspeicher, und daraus entstehen die beiden Werte,
  die der Akten-Safe braucht. Sie gehen per postMessage direkt in das
  eingebettete Fenster — im selben Browser, an keinem Server vorbei, weil sie
  gar nicht erst an einen gehen.

  Fällt irgendetwas davon aus, zeigt der Akten-Safe seine gewohnte
  Passwortmaske. Die Abkürzung darf nie der einzige Weg hinein sein.
-->
<script lang="ts">
  import { sitzung } from '../lib/sitzung.svelte.ts';
  import HilfeLink from '../lib/HilfeLink.svelte';
  import { aktenUebergabe, aktenschrankBereit } from '../lib/zugang.ts';

  /**
   * Adresse des Akten-Safes. Im Entwicklungsbetrieb der lokale Server,
   * sonst die feste Herkunft — sie muss zeichengenau der entsprechen, die
   * dort in `uebergabe.ts` erlaubt ist.
   */
  const AKTEN_HERKUNFT = import.meta.env.DEV
    ? 'http://localhost:5173'
    : 'https://akten.it-karsten-fischer.de';

  let rahmen = $state<HTMLIFrameElement | null>(null);
  let zustand = $state<'wartet' | 'uebergeben' | 'ohne-schluessel' | 'fehler'>(
    aktenschrankBereit() ? 'wartet' : 'ohne-schluessel',
  );

  /**
   * Der Akten-Safe meldet sich, sobald sein Skript läuft. Erst dann darf
   * gesendet werden — eine zu früh abgeschickte Nachricht wäre verloren, und
   * der Kunde stünde vor einer Passwortmaske, obwohl er sich gerade angemeldet
   * hat.
   */
  $effect(() => {
    // Eine benannte Funktion, kein Pfeil an Ort und Stelle: Sonst bekäme
    // removeEventListener eine andere Funktion als addEventListener, und der
    // Zuhörer bliebe für die Lebensdauer der Seite hängen.
    async function verarbeiten(event: MessageEvent): Promise<void> {
      if (event.origin !== AKTEN_HERKUNFT) return;
      if (rahmen === null || event.source !== rahmen.contentWindow) return;

      const daten = event.data as Record<string, unknown> | null;
      if (!daten || daten.typ !== 'kf-akten-bereit' || daten.version !== 1) return;

      const adresse = sitzung.konto?.email;
      if (!adresse) return;

      const uebergabe = await aktenUebergabe(adresse);
      if (!uebergabe) {
        zustand = 'ohne-schluessel';
        return;
      }

      // Zieladresse fest benannt, nie '*': Sonst bekäme jede Seite, die sich
      // zwischenschiebt, den Schlüssel des Kunden.
      rahmen.contentWindow?.postMessage(
        {
          typ: 'kf-akten-schluessel',
          version: 1,
          email: uebergabe.email,
          anmeldebeweis: uebergabe.anmeldebeweis,
          schluessel: uebergabe.schluessel,
        },
        AKTEN_HERKUNFT,
      );
      zustand = 'uebergeben';
    }

    function empfangen(event: MessageEvent): void {
      void verarbeiten(event);
    }

    window.addEventListener('message', empfangen);
    return () => window.removeEventListener('message', empfangen);
  });
</script>

<h1>Ihr Akten-Safe</h1>
<HilfeLink slug="aktenschrank-einrichten" text="Anleitung: Akten-Safe einrichten und anmelden" />

{#if zustand === 'ohne-schluessel'}
  <p class="hinweis">
    Ihr Akten-Safe fragt gleich nach Ihrem Passwort. Das passiert, wenn die Seite
    zwischendurch neu geladen wurde — Ihre Schlüssel liegen dann nicht mehr bereit. Melden Sie
    sich einfach im Akten-Safe an, oder melden Sie sich hier ab und wieder neu an.
  </p>
{/if}

<div class="rahmen">
  <iframe
    bind:this={rahmen}
    src={AKTEN_HERKUNFT}
    title="Ihr Akten-Safe"
    allow="clipboard-write"
  ></iframe>
</div>

<p class="leise klein">
  Ihre Unterlagen werden auf diesem Gerät ver- und entschlüsselt. Wir können sie nicht lesen —
  auch dann nicht, wenn Sie uns darum bitten.
</p>

<style>
  .rahmen {
    /* Der Akten-Safe bringt sein eigenes Gerüst mit; hier nur der Platz. */
    border: 1px solid var(--rand, #d5dae0);
    border-radius: 8px;
    overflow: hidden;
    background: #fff;
  }

  iframe {
    display: block;
    width: 100%;
    /* Hoch genug, dass die Dokumentenliste ohne zweiten Rollbalken auskommt. */
    height: min(78vh, 900px);
    border: 0;
  }

  .hinweis {
    padding: 0.75rem 1rem;
    border-left: 3px solid var(--warnung, #9e4429);
    background: rgba(158, 68, 41, 0.06);
    margin-bottom: 1rem;
  }
</style>
